import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import {
  ADAPTIVE_QBANK_TEST_PATH,
  goBackToAdaptiveQuestionBankListing,
} from "../../../../custom_utilities/dashboardBannerNavigation";
import images from "../../../../utilities/images/images";
import {
  fetchAdaptiveHomepageRequest,
  fetchAdaptiveAllQuestionListRequest,
  postAdaptiveLearningSummaryRequest,
} from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import structureIcon from "../../../../utilities/images/adaptive_qbank/icon.png";
import editIcon from "../../../../utilities/images/adaptive_qbank/edit_icon.png";
import "./AdaptiveNextFocusSet.css";
import { postAdaptiveSubTopicListRequest } from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import CustomizeSubtopicModal from "../CustomizeSubtopicModal";
import AdaptiveUpgradeModal from "../components/AdaptiveUpgradeModal";



const getAdaptiveCards = (homepagePayload) => {
  if (!homepagePayload) return [];
  const root = homepagePayload?.data ?? homepagePayload;
  if (!root) return [];
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.cards)) return root.cards;
  if (Array.isArray(root?.list)) return root.list;
  return [];
};

const getHomepageRoot = (payload) => payload?.data ?? payload ?? {};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const pickValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

/** Parses API values like "FS-6", 6, "6" into a positive focus-set order number. */
const parseFocusSetOrderFromApi = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  const str = String(value).trim();
  const match = str.match(/(\d+)\s*$/);
  if (match) {
    const n = Number(match[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const digits = str.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeCycleNumber = (item) => {
  const cycleRaw = String(
    pickValue(
      item?.learning_cycle,
      item?.learningCycle,
      item?.cycle,
      item?.cycle_no,
      item?.cycleNo
    ) || ""
  );
  const num = Number(cycleRaw.replace(/[^\d]/g, ""));
  return Number.isFinite(num) && num > 0 ? num : 1;
};

const isCompletedFocusSet = (item) => {
  const direct = pickValue(item?.is_completed, item?.completed, item?.isCompleted);
  if (direct === true || Number(direct) === 1) return true;
  const status = String(pickValue(item?.status, item?.state, item?.result, "")).toLowerCase();
  return status.includes("completed") || status.includes("mastered") || status.includes("done");
};

const isActiveFocusSet = (item) => {
  const direct = pickValue(item?.is_current, item?.current, item?.isActive);
  if (direct === true || Number(direct) === 1) return true;
  const status = String(pickValue(item?.status, item?.state, "")).toLowerCase();
  return (
    status.includes("current") ||
    status.includes("active") ||
    status.includes("resume")
  );
};

const isResumeFocusSet = (item) => {
  const status = String(
    pickValue(item?.status, item?.state, item?.result, "")
  ).toLowerCase();
  return status.includes("resume") || status.includes("paused");
};

const normalizeFocusSet = (item, idx) => {
  const order = Number(
    pickValue(item?.focus_set_no, item?.focusSetNo, item?.order, item?.id, idx + 1)
  );
  const perSetAdaptiveQuestionId = Number(
    pickValue(
      item?.adaptive_question_id,
      item?.adaptiveQuestionId,
      item?.paper_id,
      item?.paperId,
      item?.aqb_id
    )
  );
  return {
    label: `FS-${order}`,
    order,
    cycle: normalizeCycleNumber(item),
    completed: isCompletedFocusSet(item),
    active: isActiveFocusSet(item),
    resume: isResumeFocusSet(item),
    adaptive_question_id:
      Number.isFinite(perSetAdaptiveQuestionId) && perSetAdaptiveQuestionId > 0
        ? perSetAdaptiveQuestionId
        : null,
  };
};

const flattenFocusSetsFromLearningCycles = (root) => {
  const cycles = toArray(pickValue(root?.learning_cycles, root?.learningCycles));
  if (!cycles.length) return [];
  const merged = [];
  for (const cycleEl of cycles) {
    const cycleNum = normalizeCycleNumber(cycleEl);
    const sets = toArray(
      pickValue(
        cycleEl?.focus_sets,
        cycleEl?.focusSets,
        cycleEl?.sets,
        cycleEl?.focus_set_list,
        cycleEl?.focusSetList
      )
    );
    for (const s of sets) {
      merged.push({
        ...s,
        learning_cycle: pickValue(s?.learning_cycle, s?.learningCycle, cycleEl?.learning_cycle, cycleNum),
        cycle: pickValue(s?.cycle, cycleEl?.cycle, cycleNum),
      });
    }
  }
  return merged;
};

const getFocusSets = (homepagePayload) => {
  const root = getHomepageRoot(homepagePayload);
  let rawSets = toArray(
    pickValue(
      root?.focus_sets,
      root?.focusSets,
      root?.learning_cycle_sets,
      root?.learningCycleSets,
      root?.progress,
      root?.progress_list,
      root?.focus_set_progress
    )
  );
  if (rawSets.length === 0) {
    rawSets = flattenFocusSetsFromLearningCycles(root);
  }

  const normalizedSets = rawSets.map(normalizeFocusSet).sort((a, b) => a.order - b.order);
  if (normalizedSets.length > 0 && !normalizedSets.some((set) => set.active)) {
    const currentOrder = parseFocusSetOrderFromApi(
      pickValue(
        root?.current_focus_set,
        root?.currentFocusSet,
        root?.current_focus_set_no,
        root?.currentFocusSetNo,
        root?.active_focus_set_no,
        root?.activeFocusSetNo,
        root?.focus_set_no,
        root?.focusSetNo
      )
    );
    if (currentOrder !== null) {
      return normalizedSets.map((set) => ({
        ...set,
        active: set.order === currentOrder,
        completed: set.order < currentOrder ? true : set.completed,
      }));
    }
    const firstIncompleteIndex = normalizedSets.findIndex((set) => !set.completed);
    const fallbackActiveIndex =
      firstIncompleteIndex >= 0 ? firstIncompleteIndex : normalizedSets.length - 1;
    return normalizedSets.map((set, index) => ({
      ...set,
      active: index === fallbackActiveIndex,
    }));
  }
  return normalizedSets;
};

const getFallbackProgressMeta = (root) => {
  const cycleOneCompleted = Number(
    pickValue(
      root?.cycle_1_completed_focus_sets,
      root?.cycle1_completed_focus_sets,
      root?.cycle_1_completed_count,
      root?.cycle1_completed_count,
      root?.cycle_1_completed,
      root?.cycle1_completed,
      root?.learning_cycle_1_completed,
      root?.learningCycle1Completed,
      0
    )
  );
  const cycleTwoCompleted = Number(
    pickValue(
      root?.cycle_2_completed_focus_sets,
      root?.cycle2_completed_focus_sets,
      root?.cycle_2_completed_count,
      root?.cycle2_completed_count,
      root?.cycle_2_completed,
      root?.cycle2_completed,
      root?.learning_cycle_2_completed,
      root?.learningCycle2Completed,
      0
    )
  );
  const currentFocusSetNumeric = Number(
    pickValue(
      root?.current_focus_set_no,
      root?.currentFocusSetNo,
      root?.active_focus_set_no,
      root?.activeFocusSetNo,
      root?.focus_set_no,
      root?.focusSetNo
    )
  );
  const currentFocusSetParsed =
    Number.isFinite(currentFocusSetNumeric) && currentFocusSetNumeric > 0
      ? currentFocusSetNumeric
      : parseFocusSetOrderFromApi(
          pickValue(
            root?.current_focus_set,
            root?.currentFocusSet,
            root?.current_focus_set_label,
            root?.currentFocusSetLabel
          )
        );

  return {
    completedByCycle: {
      1: Number.isFinite(cycleOneCompleted) ? cycleOneCompleted : 0,
      2: Number.isFinite(cycleTwoCompleted) ? cycleTwoCompleted : 0,
    },
    currentFocusSet: currentFocusSetParsed,
  };
};

const buildFallbackFocusSets = (
  perCycle = 5,
  totalCycles = 2,
  completedByCycle = {},
  currentFocusSet = null
) => {
  const safePerCycle = Number.isFinite(perCycle) && perCycle > 0 ? perCycle : 5;
  const safeCycles = Number.isFinite(totalCycles) && totalCycles > 0 ? totalCycles : 2;
  const result = [];
  let order = 1;

  for (let cycle = 1; cycle <= safeCycles; cycle += 1) {
    const cycleCompleted = Number(completedByCycle?.[cycle] ?? 0);
    for (let i = 0; i < safePerCycle; i += 1) {
      const isCompleted =
        currentFocusSet !== null && Number.isFinite(currentFocusSet)
          ? order < currentFocusSet
          : i < cycleCompleted;
      result.push({
        label: `FS-${order}`,
        order,
        cycle,
        completed: isCompleted,
        active:
          currentFocusSet !== null && Number.isFinite(currentFocusSet)
            ? order === currentFocusSet
            : order === 1,
      });
      order += 1;
    }
  }

  return result;
};

const getNextCyclePlaceholderSets = (lastOrder, perCycle) => {
  const safePerCycle = Number.isFinite(perCycle) && perCycle > 0 ? perCycle : 5;
  const safeLastOrder = Number.isFinite(lastOrder) && lastOrder > 0 ? lastOrder : 0;
  const nextStart = safeLastOrder + 1;
  const nextCycleNo = Math.floor(safeLastOrder / safePerCycle) + 1;
  return Array.from({ length: safePerCycle }, (_, index) => {
    const order = nextStart + index;
    return {
      label: `FS-${order}`,
      order,
      cycle: nextCycleNo,
      completed: false,
      active: false,
    };
  });
};

const getDifficultyValue = (subtopic) => {
  const rawDifficulty = String(
    pickValue(
      subtopic?.current_difficulty,
      subtopic?.selecteddifficulty,
      subtopic?.difficulty,
      subtopic?.level,
      subtopic?.mastery_tag,
      subtopic?.subtopic_level,
      "Easy"
    ) || "Easy"
  ).toLowerCase();

  if (rawDifficulty.includes("hard")) return "Hard";
  if (rawDifficulty.includes("medium")) return "Medium";
  return "Easy";
};

const getCustomizeTopicsFromHomepage = (root) =>
  toArray(
    pickValue(
      root?.customize_topics?.topics,
      root?.customizeTopics?.topics,
      root?.customize_topics?.subtopics,
      root?.customizeTopics?.subtopics
    )
  );

const normalizeCustomizeTopicNameKey = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const coerceCustomizeTopicRowNumericId = (raw) => {
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : raw;
};

/**
 * `id` for POST adaptiveQBSubtopics_list — adaptive_homepage `customize_topics.topics[].id`.
 * 1) Match by name (modal / row labels).
 * 2) Else same index as the planner slot (topics[0] → left card, topics[1] → right) when labels differ from API.
 */
const resolveHomepageCustomizeSubtopicPayloadId = (
  homepageRoot,
  modalTopicName,
  slotTopic,
  plannerSlotIndex
) => {
  const rows = getCustomizeTopicsFromHomepage(homepageRoot);
  if (!rows.length) return null;

  const key = normalizeCustomizeTopicNameKey(
    pickValue(
      modalTopicName,
      slotTopic?.sub_topic_name,
      slotTopic?.subtopic_name,
      slotTopic?.topic_name,
      slotTopic?.name
    )
  );
  if (key) {
    const row = rows.find((r) => {
      const rk = normalizeCustomizeTopicNameKey(
        pickValue(r?.sub_topic_name, r?.subtopic_name, r?.topic_name, r?.name)
      );
      return rk && rk === key;
    });
    const byName = coerceCustomizeTopicRowNumericId(row?.id);
    if (byName !== null && byName !== undefined) return byName;
  }

  const slot = Number(plannerSlotIndex);
  if (Number.isFinite(slot) && slot >= 0 && slot < rows.length) {
    return coerceCustomizeTopicRowNumericId(rows[slot]?.id);
  }

  return null;
};

const isTopicEditable = (topicItem = {}) => {
  const editEnabled = pickValue(
    topicItem?.edit_enabled,
    topicItem?.editEnabled,
    topicItem?.enabled
  );
  return editEnabled === true || Number(editEnabled) === 1;
};

const isTopicSelectedForCustomize = (topicItem = {}) => {
  const selected = pickValue(
    topicItem?.is_selected,
    topicItem?.isSelected,
    topicItem?.selected
  );
  return selected === true || Number(selected) === 1;
};

const getTopicIdentity = (topicItem = {}) =>
  String(
    pickValue(
      topicItem?.sub_topic_id,
      topicItem?.subtopic_id,
      topicItem?.id,
      topicItem?.topic_id,
      topicItem?.subtopic_name,
      topicItem?.sub_topic_name,
      ""
    ) || ""
  ).trim();

const FOCUS_SETS_PER_LEARNING_CYCLE = 5;

const toRoman = (num) => {
  const romanMap = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let value = Number(num);
  if (!Number.isFinite(value) || value <= 0) return String(num);
  let result = "";
  for (const [base, symbol] of romanMap) {
    while (value >= base) {
      result += symbol;
      value -= base;
    }
  }
  return result;
};

const getLocalDateKey = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** When `is_user_free` is true (or equivalent), enforce free-tier rules; paid users have `false`/`0`. */
const isAdaptiveQBFreeTier = (payload) => {
  const raw = payload?.is_user_free;
  if (raw === true || Number(raw) === 1) return true;
  if (typeof raw === "string") {
    const t = raw.trim().toLowerCase();
    return t === "true" || t === "1";
  }
  return false;
};

const AdaptiveNextFocusSet = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isCustomizeEnabled, setIsCustomizeEnabled] = useState(false);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedCustomizeTopic, setSelectedCustomizeTopic] = useState(null);
  const [customizedVisibleTopics, setCustomizedVisibleTopics] = useState([]);
  const hasAutoStartedFocusSetRef = useRef(false);

  const topic = location.state?.topic;
  const pausedFocusSetNoFromRoute = Number(location?.state?.pausedFocusSetNo || 0);
  const isPausedJourneyFromRoute = location?.state?.isPaused === true;
  const suppressResumeFromRoute = location?.state?.suppressResume === true;
  const openUpgradeModalFromRoute = location?.state?.openUpgradeModal === true;
  const completedFocusSetNoFromRoute = Number(location?.state?.completedFocusSetNo || 0);
  const nextFocusSetNoFromRoute = Number(location?.state?.focusSetNo || 0);
  const autoStartFocusSetNoFromRoute = Number(location?.state?.autoStartFocusSetNo || 0);

  const topicId = topic?.topic_id;
  const subjectName = topic?.subject || topic?.sub_name || topic?.subject_name || "";

  const { adaptiveHomepage, loading, error } = useSelector(
    (state) => state.adaptiveQBank
  );
  const userId = useSelector((state) => state.auth?.user?.user_id);
  const { subTopicList } = useSelector((state) => state.adaptiveQBank);

  const subtopics = useMemo(
    () =>
      toArray(
        pickValue(
          subTopicList?.data?.subtopics,
          subTopicList?.data?.subtopic_list,
          subTopicList?.data?.sub_topic,
          subTopicList?.data?.topic_subtopics,
          subTopicList?.data?.list,
          subTopicList?.data,
          subTopicList?.subtopics,
          subTopicList?.subtopic_list,
          subTopicList?.sub_topic,
          subTopicList?.topic_subtopics,
          subTopicList?.list
        )
      ),
    [subTopicList]
  );

  useEffect(() => {
    if (!topicId || !subjectName) return;
    dispatch(
      postAdaptiveSubTopicListRequest({
        topic_id: topicId,
        subject: subjectName,
      })
    );
  }, [dispatch, topicId, subjectName]);

  useEffect(() => {
    if (!topicId || !subjectName) return;
  
    dispatch(
      fetchAdaptiveHomepageRequest({
        topic_id: topicId,
        subject: subjectName,
      })
    );
  }, [dispatch, topicId, subjectName]);

  const cards = useMemo(
    () => getAdaptiveCards(adaptiveHomepage),
    [adaptiveHomepage]
  );
  const homepageRoot = useMemo(
    () => getHomepageRoot(adaptiveHomepage),
    [adaptiveHomepage]
  );
  const topicName = useMemo(() => {
    const fromRoute = topic?.topic_name;
    if (fromRoute) return fromRoute;
    return pickValue(
      homepageRoot?.topic_name,
      homepageRoot?.topic_title,
      homepageRoot?.chapter_name,
      cards?.[0]?.topic_name,
      cards?.[0]?.chapter_name,
      cards?.[0]?.name
    );
  }, [topic, homepageRoot, cards]);
  const adaptiveQuestionIdForTopic = useMemo(
    () =>
      Number(
        pickValue(
          homepageRoot?.adaptive_question_id,
          homepageRoot?.adaptiveQuestionId,
          cards?.[0]?.adaptive_question_id,
          cards?.[0]?.adaptiveQuestionId
        )
      ) || null,
    [homepageRoot, cards]
  );
  useEffect(() => {
    if (!adaptiveQuestionIdForTopic) return;
    dispatch(
      fetchAdaptiveAllQuestionListRequest({
        paper_type: 20,
        adaptive_question_id: adaptiveQuestionIdForTopic,
      })
    );
  }, [dispatch, adaptiveQuestionIdForTopic]);
  const isFreeUser = isAdaptiveQBFreeTier(homepageRoot);
  useEffect(() => {
    if (!openUpgradeModalFromRoute || !isFreeUser) return;
    setIsUpgradeModalOpen(true);
  }, [openUpgradeModalFromRoute, isFreeUser]);
  useEffect(() => {
    if (isFreeUser) {
      setIsCustomizeEnabled(false);
    }
  }, [isFreeUser]);
  const freeDailyConsumptionKey = useMemo(() => {
    const normalizedUserKey =
      userId !== undefined &&
      userId !== null &&
      String(userId).trim() !== ""
        ? String(userId).trim()
        : "anonymous";
    return `adaptive_qbank_free_daily_consumed_global_${normalizedUserKey}_${getLocalDateKey()}`;
  }, [userId]);
  const [hasConsumedFreeDailySet, setHasConsumedFreeDailySet] = useState(false);
  useEffect(() => {
    if (!freeDailyConsumptionKey) {
      setHasConsumedFreeDailySet(false);
      return;
    }
    setHasConsumedFreeDailySet(localStorage.getItem(freeDailyConsumptionKey) === "1");
  }, [freeDailyConsumptionKey]);
  useEffect(() => {
    if (!isFreeUser || !freeDailyConsumptionKey) return;
    if (completedFocusSetNoFromRoute <= 0) return;
    localStorage.setItem(freeDailyConsumptionKey, "1");
    setHasConsumedFreeDailySet(true);
  }, [completedFocusSetNoFromRoute, freeDailyConsumptionKey, isFreeUser]);
  // Always allow one free daily attempt.
  // Show lock popup only after user has completed that free attempt today.
  const isFreeUserLocked =
    isFreeUser && (hasConsumedFreeDailySet || openUpgradeModalFromRoute);
  const focusSetAccessRaw = pickValue(
    homepageRoot?.focus_set_access,
    homepageRoot?.focusSetAccess,
    cards?.[0]?.focus_set_access,
    cards?.[0]?.focusSetAccess
  );
  const hasFocusSetAccess =
    focusSetAccessRaw === null
      ? !isFreeUserLocked
      : focusSetAccessRaw === true ||
        Number(focusSetAccessRaw) === 1 ||
        String(focusSetAccessRaw).toLowerCase() === "true";
  const freeFocusSetInfo =
    homepageRoot?.focus_set_label_free || homepageRoot?.focusSetLabelFree || {};
  const focusSets = useMemo(() => {
    const perCycle = Number(
      pickValue(
        homepageRoot?.focus_sets_per_cycle,
        homepageRoot?.focusSetPerCycle,
        homepageRoot?.focus_set_per_cycle,
        homepageRoot?.learning_cycle_size,
        5
      )
    );
    const cycles = Number(
      pickValue(
        homepageRoot?.total_learning_cycles,
        homepageRoot?.total_cycles,
        homepageRoot?.learningCycles,
        2
      )
    );
    const progressMeta = getFallbackProgressMeta(homepageRoot);
    const fallbackSets = buildFallbackFocusSets(
      perCycle,
      cycles,
      progressMeta.completedByCycle,
      progressMeta.currentFocusSet
    );
    const parsed = getFocusSets(adaptiveHomepage);
    if (parsed.length === 0) return fallbackSets;

    // Always preserve full cycle layout (e.g. FS-1..FS-10) and layer API state on top.
    const parsedByOrder = new Map(parsed.map((set) => [set.order, set]));
    const merged = fallbackSets.map((baseSet) => {
      const apiSet = parsedByOrder.get(baseSet.order);
      if (!apiSet) return baseSet;
      return {
        ...baseSet,
        ...apiSet,
        // Keep fallback label/order/cycle if API misses them.
        label: apiSet.label || baseSet.label,
        order: Number.isFinite(apiSet.order) ? apiSet.order : baseSet.order,
        cycle: Number.isFinite(apiSet.cycle) ? apiSet.cycle : baseSet.cycle,
      };
    });
    const fallbackOrders = new Set(fallbackSets.map((set) => set.order));
    const parsedOutsideFallback = parsed.filter((set) => !fallbackOrders.has(set.order));
    const mergedWithAllParsed = [...merged, ...parsedOutsideFallback].sort(
      (a, b) => a.order - b.order
    );

    const hasActive = mergedWithAllParsed.some((set) => set.active);
    if (hasActive) return mergedWithAllParsed;

    const activeOrder = progressMeta.currentFocusSet;
    if (Number.isFinite(activeOrder) && activeOrder > 0) {
      return mergedWithAllParsed.map((set) => ({
        ...set,
        active: set.order === activeOrder,
        completed: set.order < activeOrder ? true : set.completed,
      }));
    }

    return mergedWithAllParsed;
  }, [adaptiveHomepage, homepageRoot]);
  const displayFocusSets = useMemo(() => {
    const baseSets = focusSets;

    // Optimistic UI sync right after submit:
    // mark submitted set done and highlight next set as current.
    let syncedSets = baseSets;
    if (completedFocusSetNoFromRoute > 0 || nextFocusSetNoFromRoute > 0) {
      syncedSets = baseSets.map((set) => ({
        ...set,
        completed:
          set.completed ||
          (completedFocusSetNoFromRoute > 0 && set.order === completedFocusSetNoFromRoute),
        active: nextFocusSetNoFromRoute > 0 ? set.order === nextFocusSetNoFromRoute : set.active,
      }));
    }

    const perCycle = Number(
      pickValue(
        homepageRoot?.focus_sets_per_cycle,
        homepageRoot?.focusSetPerCycle,
        homepageRoot?.focus_set_per_cycle,
        homepageRoot?.learning_cycle_size,
        FOCUS_SETS_PER_LEARNING_CYCLE
      )
    );
    const safePerCycle =
      Number.isFinite(perCycle) && perCycle > 0 ? perCycle : FOCUS_SETS_PER_LEARNING_CYCLE;
    const lastAvailableOrder = syncedSets.reduce(
      (maxOrder, set) => (set.order > maxOrder ? set.order : maxOrder),
      0
    );
    const highestReachedOrder = Math.max(
      completedFocusSetNoFromRoute || 0,
      nextFocusSetNoFromRoute || 0,
      parseFocusSetOrderFromApi(homepageRoot?.current_focus_set) || 0,
      Number(homepageRoot?.current_focus_set_no) || 0,
      Number(homepageRoot?.currentFocusSetNo) || 0
    );

    if (highestReachedOrder > lastAvailableOrder) {
      const appendedSets = [
        ...syncedSets,
        ...getNextCyclePlaceholderSets(lastAvailableOrder, safePerCycle),
      ];
      const targetActiveOrder =
        nextFocusSetNoFromRoute > 0 ? nextFocusSetNoFromRoute : highestReachedOrder;
      return appendedSets.map((set) => ({
        ...set,
        active:
          Number.isFinite(targetActiveOrder) && targetActiveOrder > 0
            ? set.order === targetActiveOrder
            : set.active,
        completed:
          Number.isFinite(targetActiveOrder) && targetActiveOrder > 0
            ? set.order < targetActiveOrder
              ? true
              : set.completed
            : set.completed,
      }));
    }

    return syncedSets;
  }, [
    focusSets,
    completedFocusSetNoFromRoute,
    nextFocusSetNoFromRoute,
    homepageRoot,
  ]);

  const pausedFocusSetNoFromStorage = useMemo(() => {
    if (!adaptiveQuestionIdForTopic) return 0;
    const storedValue = Number(
      localStorage.getItem(
        `adaptive_qbank_paused_focus_set_${adaptiveQuestionIdForTopic}`
      ) || 0
    );
    return Number.isFinite(storedValue) && storedValue > 0 ? storedValue : 0;
  }, [adaptiveQuestionIdForTopic]);
  const hasResumeFromStorage = pausedFocusSetNoFromStorage > 0;
  const resumeFocusSetNoFromApi = useMemo(() => {
    const resumeSet = displayFocusSets.find(
      (set) => set.resume === true && !set.completed
    );
    return resumeSet?.order || 0;
  }, [displayFocusSets]);
  const hasResumeState = hasResumeFromStorage || resumeFocusSetNoFromApi > 0;
  const isPausedJourney =
    isPausedJourneyFromRoute || (!suppressResumeFromRoute && hasResumeState);
  const isCustomizeLockedForResume = isPausedJourney;
  const normalizedDisplayFocusSets = useMemo(() => {
    if (displayFocusSets.length === 0) return displayFocusSets;
    if (isPausedJourney) return displayFocusSets;

    const hasExplicitActive = displayFocusSets.some((set) => set.active);
    if (hasExplicitActive) return displayFocusSets;

    const firstIncompleteIndex = displayFocusSets.findIndex((set) => !set.completed);
    const activeIndex =
      firstIncompleteIndex >= 0 ? firstIncompleteIndex : displayFocusSets.length - 1;

    return displayFocusSets.map((set, index) => ({
      ...set,
      active: index === activeIndex,
    }));
  }, [displayFocusSets, isPausedJourney]);
  const cycleOne = normalizedDisplayFocusSets.slice(0, FOCUS_SETS_PER_LEARNING_CYCLE);
  const cycleTwo = normalizedDisplayFocusSets.slice(
    FOCUS_SETS_PER_LEARNING_CYCLE,
    FOCUS_SETS_PER_LEARNING_CYCLE * 2
  );
  const currentFocusSetNo = useMemo(() => {
    const activeSet = normalizedDisplayFocusSets.find((set) => set.active);
    return activeSet?.order || normalizedDisplayFocusSets?.[0]?.order || 1;
  }, [normalizedDisplayFocusSets]);
  const currentActiveFocusSetAdaptiveId = useMemo(() => {
    const activeSet = normalizedDisplayFocusSets.find((set) => set.active);
    const activeSetAdaptiveId = Number(activeSet?.adaptive_question_id);
    if (Number.isFinite(activeSetAdaptiveId) && activeSetAdaptiveId > 0) {
      return activeSetAdaptiveId;
    }
    return null;
  }, [normalizedDisplayFocusSets]);
  const pausedFocusSetNo =
    pausedFocusSetNoFromRoute > 0
      ? pausedFocusSetNoFromRoute
      : pausedFocusSetNoFromStorage > 0
        ? pausedFocusSetNoFromStorage
      : resumeFocusSetNoFromApi > 0
        ? resumeFocusSetNoFromApi
      : isPausedJourney
        ? currentFocusSetNo
        : 0;
  const completedCycleOne = cycleOne.filter((set) => set.completed).length;
  const completedCycleTwo = cycleTwo.filter((set) => set.completed).length;

  const globalRequiredPerCycle = Number(
    pickValue(
      homepageRoot?.focus_sets_per_cycle,
      homepageRoot?.focusSetPerCycle,
      homepageRoot?.focus_set_per_cycle,
      homepageRoot?.learning_cycle_size
    )
  );
  const cycleOneRequired = Number(
    pickValue(
      homepageRoot?.cycle_1_focus_sets_required,
      homepageRoot?.cycle1_focus_sets_required,
      homepageRoot?.cycleOneFocusSetsRequired,
      Number.isFinite(globalRequiredPerCycle) && globalRequiredPerCycle > 0
        ? globalRequiredPerCycle
        : cycleOne.length
    )
  );
  const cycleTwoRequired = Number(
    pickValue(
      homepageRoot?.cycle_2_focus_sets_required,
      homepageRoot?.cycle2_focus_sets_required,
      homepageRoot?.cycleTwoFocusSetsRequired,
      Number.isFinite(globalRequiredPerCycle) && globalRequiredPerCycle > 0
        ? globalRequiredPerCycle
        : cycleTwo.length
    )
  );

  const canOpenCycleOneInsights =
    cycleOneRequired > 0 && completedCycleOne >= cycleOneRequired;
  const canOpenCycleTwoInsights =
    cycleTwoRequired > 0 && completedCycleTwo >= cycleTwoRequired;
  const cycleGroups = useMemo(() => {
    const sortedSets = [...normalizedDisplayFocusSets].sort((a, b) => a.order - b.order);
    const groups = [];
    for (let index = 0; index < sortedSets.length; index += FOCUS_SETS_PER_LEARNING_CYCLE) {
      const cycleNo = Math.floor(index / FOCUS_SETS_PER_LEARNING_CYCLE) + 1;
      groups.push({
        cycleNo,
        title: `Learning Cycle - ${toRoman(cycleNo)}`,
        sets: sortedSets.slice(index, index + FOCUS_SETS_PER_LEARNING_CYCLE),
      });
    }
    return groups;
  }, [normalizedDisplayFocusSets]);
  const canOpenCycleInsightsMap = useMemo(() => {
    const map = new Map();
    cycleGroups.forEach(({ cycleNo, sets }) => {
      if (cycleNo === 1) {
        map.set(cycleNo, canOpenCycleOneInsights);
        return;
      }
      if (cycleNo === 2) {
        map.set(cycleNo, canOpenCycleTwoInsights);
        return;
      }
      map.set(cycleNo, sets.length > 0 && sets.every((set) => set.completed));
    });
    return map;
  }, [cycleGroups, canOpenCycleOneInsights, canOpenCycleTwoInsights]);
  const defaultStartTopics = useMemo(() => {
    const customizeTopics = getCustomizeTopicsFromHomepage(homepageRoot);

    return customizeTopics.slice(0, 2);
  }, [homepageRoot]);
  const enabledCustomizeTopicsFromHomepage = useMemo(() => {
    const customizeTopics = getCustomizeTopicsFromHomepage(homepageRoot);
    return customizeTopics.filter((topicItem) => isTopicEditable(topicItem));
  }, [homepageRoot]);
  const enabledCustomizeTopics = useMemo(() => {
    const deduped = [];
    const seen = new Set();
    const addTopics = (list = []) => {
      list.forEach((topicItem) => {
        const identity = getTopicIdentity(topicItem);
        if (!identity || seen.has(identity)) return;
        if (!isTopicEditable(topicItem)) return;
        seen.add(identity);
        deduped.push(topicItem);
      });
    };

    if (subtopics.length > 0) {
      const selectedTopics = subtopics.filter((topicItem) =>
        isTopicSelectedForCustomize(topicItem) && isTopicEditable(topicItem)
      );
      const editableTopics = subtopics.filter((topicItem) => isTopicEditable(topicItem));
      addTopics(selectedTopics);
      addTopics(editableTopics);
    }
    addTopics(enabledCustomizeTopicsFromHomepage);

    return deduped.slice(0, 2);
  }, [subtopics, enabledCustomizeTopicsFromHomepage]);
  const structureInfo = useMemo(() => {
    const section =
      homepageRoot?.structure_info ||
      homepageRoot?.structureInfo ||
      {};

    const title = section?.title || "How this practice is structured";
    const points = toArray(section?.points);

    return {
      title,
      points: points.length
        ? points
        : [
            "5 Focus Sets form 1 Learning Cycle",
            "Each Focus Set has 10 questions from 2 subtopics",
            "Difficulty adapts as you progress",
          ],
    };
  }, [homepageRoot]);
  useEffect(() => {
    if (!isCustomizeEnabled) {
      setCustomizedVisibleTopics([]);
      return;
    }
    setCustomizedVisibleTopics((prev) => {
      if (prev.length > 0) return prev;
      return enabledCustomizeTopics.slice(0, 2);
    });
  }, [enabledCustomizeTopics, isCustomizeEnabled]);
  useEffect(() => {
    if (!isCustomizeLockedForResume || !isCustomizeEnabled) return;
    setIsCustomizeEnabled(false);
  }, [isCustomizeEnabled, isCustomizeLockedForResume]);

  const showFreeFocusSetBanner = !hasFocusSetAccess;
  const shouldShowFreeUserInfoBanner = isFreeUser;
  const visibleStartTopics = isCustomizeEnabled
    ? (customizedVisibleTopics.length > 0 ? customizedVisibleTopics : enabledCustomizeTopics)
    : defaultStartTopics;
  const modalExcludedTopicIds = useMemo(() => {
    if (!selectedCustomizeTopic) return [];
    const selectedIdentity = getTopicIdentity(selectedCustomizeTopic);
    return visibleStartTopics
      .map((topicItem) => getTopicIdentity(topicItem))
      .filter((identity) => identity && identity !== selectedIdentity);
  }, [selectedCustomizeTopic, visibleStartTopics]);
  const customizeModalTopics = useMemo(() => {
    if (subtopics.length > 0) return subtopics;
    return enabledCustomizeTopics;
  }, [subtopics, enabledCustomizeTopics]);
  const shouldScrollStartTopics = visibleStartTopics.length > 2;
  const selectedTopicsForSummary = useMemo(() => {
    if (!isCustomizeEnabled) return [];
    return visibleStartTopics.slice(0, 2).map((subtopic, idx) => ({
      id: pickValue(
        subtopic?.sub_topic_id,
        subtopic?.subtopic_id,
        subtopic?.topic_id,
        subtopic?.id,
        idx + 1
      ),
      name: pickValue(
        subtopic?.subtopic_name,
        subtopic?.sub_topic_name,
        subtopic?.name,
        `Section ${idx + 1}`
      ),
      difficulty: pickValue(
        subtopic?.current_difficulty,
        subtopic?.selecteddifficulty,
        subtopic?.difficulty,
        subtopic?.level,
        "Medium"
      ),
    }));
  }, [isCustomizeEnabled, visibleStartTopics]);
  const upgradePackageId = useMemo(
    () =>
      Number(
        pickValue(
          homepageRoot?.package_details?.package_id,
          homepageRoot?.package_details?.id,
          homepageRoot?.package_detail?.package_id,
          homepageRoot?.package_detail?.id,
          homepageRoot?.package_id,
          homepageRoot?.packageId,
          cards?.[0]?.package_details?.package_id,
          cards?.[0]?.package_details?.id,
          cards?.[0]?.package_id,
          cards?.[0]?.packageId
        )
      ) || null,
    [homepageRoot, cards]
  );
  const handleUpgradeCtaClick = () => {
    setIsUpgradeModalOpen(false);
    if (upgradePackageId) {
      history.push(`/package/${upgradePackageId}`);
      return;
    }
    history.push("/packages");
  };

  const handleOpenCustomizeModal = (subtopic, slotIndex) => {
    if (isFreeUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    const editEnabled = pickValue(
      subtopic?.edit_enabled,
      subtopic?.editEnabled,
      subtopic?.enabled,
      true
    );
    if (!(editEnabled === true || Number(editEnabled) === 1)) {
      return;
    }

    setSelectedCustomizeTopic({ ...subtopic, __slotIndex: slotIndex });
    setIsCustomizeModalOpen(true);
  };

  const handleCloseCustomizeModal = () => {
    setIsCustomizeModalOpen(false);
    setSelectedCustomizeTopic(null);
  };

  const handleSaveCustomizeModal = ({
    topicId: selectedSubtopicId,
    difficulty: selectedDifficulty,
    topic: selectedTopicFromModal,
  }) => {
    if (!topicId || !subjectName || !selectedSubtopicId || !selectedDifficulty) {
      handleCloseCustomizeModal();
      return;
    }

    const homepageCustomizeId = resolveHomepageCustomizeSubtopicPayloadId(
      homepageRoot,
      selectedTopicFromModal?.name,
      selectedCustomizeTopic,
      selectedCustomizeTopic?.__slotIndex
    );
    const idPayload =
      homepageCustomizeId !== null && homepageCustomizeId !== undefined
        ? homepageCustomizeId
        : selectedSubtopicId;

    dispatch(
      postAdaptiveSubTopicListRequest({
        subject: subjectName,
        topic_id: topicId,
        selected_subtopic_id: selectedSubtopicId,
        id: idPayload,
        selecteddifficulty: String(selectedDifficulty).toUpperCase(),
      })
    );

    dispatch(
      fetchAdaptiveHomepageRequest({
        topic_id: topicId,
        subject: subjectName,
      })
    );
    if (adaptiveQuestionIdForTopic) {
      dispatch(
        fetchAdaptiveAllQuestionListRequest({
          paper_type: 20,
          adaptive_question_id: adaptiveQuestionIdForTopic,
        })
      );
    }

    setCustomizedVisibleTopics((prev) => {
      const baseList =
        Array.isArray(prev) && prev.length > 0 ? [...prev] : enabledCustomizeTopics.slice(0, 2);
      if (!baseList.length) return prev;
      const selectedId = String(selectedSubtopicId);
      const selectedTopic = selectedTopicFromModal
        ? {
            ...selectedTopicFromModal,
            subtopic_id: selectedTopicFromModal?.id || selectedSubtopicId,
            sub_topic_id: selectedTopicFromModal?.id || selectedSubtopicId,
            subtopic_name: selectedTopicFromModal?.name || selectedTopicFromModal?.subtopic_name,
            current_difficulty: selectedDifficulty,
            selecteddifficulty: selectedDifficulty,
          }
        : baseList.find((item) => getTopicIdentity(item) === selectedId);
      if (!selectedTopic) return prev;

      const slotIndex = Number(selectedCustomizeTopic?.__slotIndex);
      const nextList = [...baseList];
      if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= nextList.length) {
        return prev;
      }
      const duplicateIndex = nextList.findIndex(
        (item, idx) => idx !== slotIndex && getTopicIdentity(item) === selectedId
      );
      if (duplicateIndex >= 0) return prev;
      nextList[slotIndex] = {
        ...nextList[slotIndex],
        ...selectedTopic,
      };
      return nextList;
    });

    handleCloseCustomizeModal();
  };
  const handleFocusSetClick = (item) => {
    const isPausedSet = (pausedFocusSetNo === item.order || item.resume === true) && !item.completed;
    const isSubmittedFocusSet = item.completed === true;
    const shouldOpenSummary = isSubmittedFocusSet;
    if (!item.active && !isPausedSet && !shouldOpenSummary) return;

    if (shouldOpenSummary) {
      const adaptiveQuestionIdForSummary =
        Number(item?.adaptive_question_id) ||
        adaptiveQuestionIdForTopic ||
        Number(
          pickValue(
            location?.state?.adaptive_question_id,
            homepageRoot?.adaptive_question_id,
            homepageRoot?.adaptiveQuestionId
          )
        ) ||
        null;
      if (!adaptiveQuestionIdForSummary) return;
      history.push({
        pathname: "/practice/adaptive-question-bank/submit",
        state: {
          adaptive_question_id: adaptiveQuestionIdForSummary,
          paper_type: 20,
          topic,
          topic_id: topicId,
          focusSetNo: item.order,
        },
      });
      return;
    }

    const isFreeFirstSet = isFreeUser && item.order === 1 && !isFreeUserLocked;
    const adaptiveQuestionIdOverride = Number(
      pickValue(location?.state?.adaptive_question_id, location?.state?.adaptiveQuestionId)
    );
    const adaptiveQuestionIdForTest =
      Number(item?.adaptive_question_id) ||
      (Number.isFinite(adaptiveQuestionIdOverride) && adaptiveQuestionIdOverride > 0
        ? adaptiveQuestionIdOverride
        : undefined) ||
      adaptiveQuestionIdForTopic ||
      undefined;
    if (showFreeFocusSetBanner) {
      setIsUpgradeModalOpen(true);
      return;
    }
    history.push({
      pathname: ADAPTIVE_QBANK_TEST_PATH,
      state: {
        useAdaptiveTestScreen: true,
        isResume:
          isPausedJourney &&
          (pausedFocusSetNo === item.order || item.resume === true),
        adaptive_question_id: adaptiveQuestionIdForTest,
        paper_type: 20,
        topic,
        topic_id: topicId,
        focusSetNo: item.order,
        summarySelectedTopics: selectedTopicsForSummary,
        isTopicCustomizationApplied: selectedTopicsForSummary.length > 0,
        // Keep existing free-user flow while still opening FS-1 questions.
        noSetPresent: !isFreeFirstSet,
      },
    });
  };

  useEffect(() => {
    if (hasAutoStartedFocusSetRef.current) return;
    if (!autoStartFocusSetNoFromRoute || autoStartFocusSetNoFromRoute <= 0) return;
    if (!normalizedDisplayFocusSets.length) return;
    if (loading?.adaptiveHomepage) return;

    const targetSet = normalizedDisplayFocusSets.find(
      (set) => Number(set.order) === Number(autoStartFocusSetNoFromRoute)
    );
    if (!targetSet) return;

    const targetSetAdaptiveId = Number(targetSet?.adaptive_question_id);
    const routeAdaptiveIdOverride = Number(
      pickValue(location?.state?.adaptive_question_id, location?.state?.adaptiveQuestionId)
    );
    const hasValidAdaptiveId =
      Number.isFinite(targetSetAdaptiveId) && targetSetAdaptiveId > 0
        ? true
        : Number.isFinite(routeAdaptiveIdOverride) && routeAdaptiveIdOverride > 0;
    if (!hasValidAdaptiveId) return;

    hasAutoStartedFocusSetRef.current = true;
    handleFocusSetClick(targetSet);
  }, [
    autoStartFocusSetNoFromRoute,
    handleFocusSetClick,
    loading?.adaptiveHomepage,
    normalizedDisplayFocusSets,
  ]);

  const handleOpenLearningInsights = (cycleNo) => {
    if (!topicId || !subjectName || !cycleNo) return;
    if (isFreeUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    dispatch(
      postAdaptiveLearningSummaryRequest({
        subject: subjectName,
        topic_id: topicId,
        learning_cycle: cycleNo,
      })
    );
    history.push({
      pathname: "/practice/adaptive-question-bank/learning-summary",
      state: {
        topic,
        topic_id: topicId,
        subject: subjectName,
        learning_cycle: cycleNo,
        focusSetNo: currentFocusSetNo,
      },
    });
  };
  const handleCustomizeToggle = () => {
    if (isFreeUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    if (isPausedJourney) {
      return;
    }
    setIsCustomizeEnabled((prev) => !prev);
  };
  useEffect(() => {
    if (isPausedJourney && isCustomizeEnabled) {
      setIsCustomizeEnabled(false);
    }
  }, [isPausedJourney, isCustomizeEnabled]);
  const isPageLoading =
    Boolean(loading?.adaptiveHomepage) || Boolean(loading?.allQuestionList);

  const renderCycle = ({
    title,
    sets,
    canOpenInsights,
    showCompletedBadge,
    cycleNo,
  }) => (
    <div className="aqb-next-cycle-col" key={`cycle-${cycleNo}`}>
      <div className="aqb-next-cycle-head">
        <h5>{title}</h5>
        {showCompletedBadge ? (
          <span className="aqb-cycle-completed">Completed</span>
        ) : null}
      </div>
      <div className="aqb-next-dots">
        {sets.map((item) => (
          (() => {
            const isPausedSet =
              (pausedFocusSetNo === item.order || item.resume === true) &&
              !item.completed;
            const canOpenCompletedSummary = item.completed === true;
            const isFocusSetClickable =
              canOpenCompletedSummary ||
              (!showFreeFocusSetBanner && (item.active || isPausedSet));
            return (
          <button
            type="button"
            key={item.label}
            className={`aqb-dot ${item.completed ? "completed" : ""} ${
              item.active ? "active" : ""
            } ${isPausedSet ? "paused" : ""}`}
            disabled={!isFocusSetClickable}
            onClick={() => handleFocusSetClick(item)}
            aria-label={
              isFocusSetClickable
                ? canOpenCompletedSummary
                  ? `View ${item.label} summary`
                  : `Open ${item.label}`
                : `${item.label} (locked)`
            }
          >
            {item.completed ? (
              <img
                className="aqb-dot-completed-image"
                src={images.completed_focus_set}
                alt=""
              />
            ) : isPausedSet ? (
              <img
                className="aqb-dot-completed-image"
                src={images.pause_icon}
                alt=""
              />
            ) : null}
            <span className="aqb-dot-number">{item.order}</span>
            <span className="aqb-dot-label">{item.label}</span>
          </button>
            );
          })()
        ))}
      </div>
      <button
        type="button"
        className={`aqb-insights-btn ${canOpenInsights ? "" : "disabled"}`}
        disabled={!canOpenInsights}
        onClick={() => handleOpenLearningInsights(cycleNo)}
      >
        View learning insights
      </button>
    </div>
  );

  return (
    <div className="aqb-next-page">
      <div className="aqb-next-header">
        <img
          className="aqb-next-back"
          src={images.new_backarrow}
          alt="Back"
          onClick={() =>
            goBackToAdaptiveQuestionBankListing(history, location, {
              selectedSubject: subjectName,
            })
          }
        />
        <h2>Adaptive Question Bank</h2>
      </div>

      <div className="aqb-next-body">
        {isPageLoading ? (
          <div className="aqb-next-page-loader" role="status" aria-live="polite">
            <ClipLoader size={42} color="#C61C26" />
          </div>
        ) : (
        <section className="aqb-next-main">
          <div className="aqb-next-top-card">
            <div>
            <h3>{topicName || "Topic"}</h3>
            <p className="aqb-topic-desc">
              Focused practice built from unlimited questions
            </p>
            </div>
            <div className="aqb-next-stats">
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-purple">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.question_per_focus_set}
                    alt=""
                  />
                </span>
                <span>10 Questions</span>
                <small>Per Focus Set</small>
              </div>
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-blue">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.unlimited_focus_sets}
                    alt=""
                  />
                </span>
                <span>Unlimited</span>
                <small>Focus Sets</small>
              </div>
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-gold">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.difficulty_levels}
                    alt=""
                  />
                </span>
                <span>3 Difficulty</span>
                <small>Levels</small>
              </div>
            </div>
          </div>
          {shouldShowFreeUserInfoBanner ? (
            <div className="aqb-free-focus-banner">
              <div className="aqb-free-focus-banner__content">
                <p className="aqb-free-focus-banner__title">
                  {freeFocusSetInfo?.message || "Today's free Focus Set is complete."}
                </p>
                <p className="aqb-free-focus-banner__subtitle">
                  {freeFocusSetInfo?.unlock_msg || "Unlock unlimited practice."}
                </p>
              </div>
              <button
                type="button"
                className="aqb-free-focus-banner__button"
                onClick={() => setIsUpgradeModalOpen(true)}
                aria-label="Unlock unlimited practice"
              >
                {freeFocusSetInfo?.button_text || "Unlock"}
              </button>
            </div>
          ) : null}

          <div className="aqb-next-content-row">
            <div className="aqb-next-progress-wrap">
              <div className="aqb-next-progress-head">
                <h4>Your Progress</h4>
                <button
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname: "/practice/adaptive-question-bank/subtopic-progress",
                      state: { topic },
                    })
                  }
                >
                  Topic Mastery List
                </button>
              </div>

              <div
                className="aqb-next-cycle"
                style={{
                  "--aqb-cycle-count": String(Math.max(cycleGroups.length, 1)),
                }}
              >
                {cycleGroups.map((group) => {
                  const canOpenInsights = Boolean(
                    canOpenCycleInsightsMap.get(group.cycleNo)
                  );
                  return renderCycle({
                    title: group.title,
                    sets: group.sets,
                    canOpenInsights,
                    showCompletedBadge: canOpenInsights,
                    cycleNo: group.cycleNo,
                  });
                })}
              </div>

              <div className="aqb-next-structure">
                <h6>
                  <img src={structureIcon} alt="" aria-hidden="true" className="aqb-next-structure-icon" />
                  <span>{structureInfo.title}</span>
                </h6>
                {structureInfo.points.map((point, index) => (
                  <p key={`${point}-${index}`}>- {point}</p>
                ))}
              </div>
            </div>

            <aside className="aqb-next-side-card">
              <h4>Start Focus Set</h4>
              {isFreeUser ? (
                <div
                  className="aqb-next-toggle aqb-next-toggle--free-locked"
                  role="button"
                  tabIndex={0}
                  onClick={handleCustomizeToggle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCustomizeToggle();
                    }
                  }}
                  aria-label="Customize Topics — upgrade to unlock"
                >
                  <span>Customize Topics</span>
                  <span
                    className="aqb-switch-toggle aqb-switch-toggle--decorative"
                    aria-hidden
                  >
                    <span className="aqb-switch disabled" />
                  </span>
                </div>
              ) : (
                <div className="aqb-next-toggle">
                  <span>Customize Subtopics</span>
                  <button
                    type="button"
                    className="aqb-switch-toggle"
                    onClick={handleCustomizeToggle}
                    disabled={isPausedJourney}
                    aria-pressed={isCustomizeEnabled}
                    aria-label={
                      isPausedJourney
                        ? "Customize topics disabled while resume is active"
                        : "Toggle customize topics"
                    }
                  >
                    <span className={`aqb-switch ${isCustomizeEnabled ? "enabled" : "disabled"}`} />
                  </button>
                </div>
              )}

              <div className={`aqb-subtopic-list ${shouldScrollStartTopics ? "scrollable" : ""}`}>
                {isCustomizeEnabled ? (
                  visibleStartTopics.length > 0 ? (
                    visibleStartTopics.map((sub, idx) => {
                      const isEditEnabled =
                        pickValue(sub?.edit_enabled, sub?.editEnabled, sub?.enabled) === true ||
                        Number(pickValue(sub?.edit_enabled, sub?.editEnabled, sub?.enabled)) === 1;
                      return (
                      <div
                        className="aqb-topic-item"
                        key={
                          sub.subtopic_id ||
                          sub["sub_topic_id"] ||
                          sub.id ||
                          sub.subtopic_name ||
                          sub["sub topic name"]
                        }
                      >
                        <div className="aqb-topic-item-content">
                          <p>
                            {sub.subtopic_name ||
                              sub["sub topic name"] ||
                              sub.sub_topic_name ||
                              sub.name ||
                              "Untitled Subtopic"}
                          </p>
                          <div className="aqb-topic-tags">
                            <span>{getDifficultyValue(sub).toUpperCase()}</span>
                            <span>
                              {sub.ui_status ||
                                sub.subtopic_status ||
                                sub.status ||
                                "Not Started"}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="aqb-topic-edit"
                          onClick={() => handleOpenCustomizeModal(sub, idx)}
                          disabled={!isEditEnabled || isPausedJourney}
                          aria-disabled={!isEditEnabled || isPausedJourney}
                          aria-label={
                            isPausedJourney
                              ? "Customize topics disabled while resume is active"
                              : isEditEnabled
                                ? "Edit subtopic"
                                : "This topic is not editable"
                          }
                        >
                          <img src={editIcon} alt="" aria-hidden="true" />
                        </button>
                      </div>
                      );
                    })
                  ) : loading?.subTopicList ? (
                    <p className="aqb-customize-hint">Loading subtopics...</p>
                  ) : (
                    <p className="aqb-customize-hint">No customizable topics found</p>
                  )
                ) : defaultStartTopics.length > 0 ? (
                  defaultStartTopics.map((sub) => (
                    <div
                      className="aqb-topic-item"
                      key={
                        sub.subtopic_id ||
                        sub["sub_topic_id"] ||
                        sub.id ||
                        sub.subtopic_name ||
                        sub.sub_topic_name
                      }
                    >
                      <div className="aqb-topic-item-content">
                        <p>
                          {sub.subtopic_name ||
                            sub["sub topic name"] ||
                            sub.sub_topic_name ||
                            sub.name ||
                            "Untitled Subtopic"}
                        </p>
                        <div className="aqb-topic-tags">
                          <span>{getDifficultyValue(sub).toUpperCase()}</span>
                          <span>
                            {sub.ui_status ||
                              sub.subtopic_status ||
                              sub.status ||
                              "Not Started"}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="aqb-topic-edit"
                        disabled
                        aria-disabled="true"
                        aria-label="Enable Customize Topics to edit"
                        style={{ opacity: 0.55, cursor: "not-allowed" }}
                      >
                        <img
                          src={editIcon}
                          alt=""
                          aria-hidden="true"
                          style={{ filter: "grayscale(100%) brightness(0.72)" }}
                        />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="aqb-customize-hint">
                    Enable Customize Topics to view subtopics for this topic.
                  </p>
                )}
              </div>

              <button
                type="button"
                className={`aqb-next-start-btn ${isPausedJourney ? "resume" : ""}`}
                onClick={() => {
                  if (showFreeFocusSetBanner) {
                    setIsUpgradeModalOpen(true);
                    return;
                  }
                  history.push({
                    pathname: ADAPTIVE_QBANK_TEST_PATH,
                    state: {
                      useAdaptiveTestScreen: true,
                      isResume: isPausedJourney,
                      adaptive_question_id:
                        currentActiveFocusSetAdaptiveId ||
                        adaptiveQuestionIdForTopic ||
                        undefined,
                      paper_type: 20,
                      topic,
                      topic_id: topicId,
                      focusSetNo:
                        pausedFocusSetNo > 0
                          ? pausedFocusSetNo
                          : currentFocusSetNo,
                      summarySelectedTopics: selectedTopicsForSummary,
                      isTopicCustomizationApplied: selectedTopicsForSummary.length > 0,
                    },
                  });
                }}
              >
                {showFreeFocusSetBanner
                  ? freeFocusSetInfo?.button_text || "Unlock"
                  : isPausedJourney
                    ? "Resume"
                    : "Start Next Focus Set"}
              </button>

            </aside>
          </div>
        </section>
        )}
      </div>
      <CustomizeSubtopicModal
        isOpen={isCustomizeModalOpen}
        onClose={handleCloseCustomizeModal}
        onSave={handleSaveCustomizeModal}
        topics={customizeModalTopics}
        excludedTopicIds={modalExcludedTopicIds}
        initialTopicId={
          selectedCustomizeTopic?.subtopic_id ||
          selectedCustomizeTopic?.sub_topic_id ||
          selectedCustomizeTopic?.id ||
          ""
        }
        initialTopicName={
          selectedCustomizeTopic?.subtopic_name ||
          selectedCustomizeTopic?.sub_topic_name ||
          selectedCustomizeTopic?.name ||
          ""
        }
        initialDifficulty={getDifficultyValue(selectedCustomizeTopic)}
      />
      <AdaptiveUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onCtaClick={handleUpgradeCtaClick}
      />
    </div>
  );
};

export default AdaptiveNextFocusSet;
